/**
 * «New shift» operations whose outcome is not known yet (issue #157). Every create from the shift
 * editor carries an operation id; sending the same id and body again makes the server answer with
 * the shift the first request made, or make it once, never twice (backend/services/createOps.js).
 *
 * This browser keeps what it sent until the answer is known, so a reload can resume the same
 * operation; the server lists the ones it recorded without a shift, so another device can too.
 * Both are offered on Vaktplan (PendingCreates) with one action: send it again, same id.
 */
import { get, post } from '@/api/client';
import type { ApiShift } from '@/api/data';
import type { ShiftWrite } from '@/api/shifts';
import { T } from '@/i18n';
import { refused } from '@/pages/vaktplan/lock';
import { useQuery } from '@tanstack/react-query';

const KEY = 'timefly.createOps';
export interface CreateOp {
  op_id: string;
  body: ShiftWrite;
  at: string;
}
type Stored = CreateOp & { tenant: number };

export const newOpId = () => crypto.randomUUID();

const read = (): Stored[] => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};
const write = (all: Stored[]) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(all.slice(-50)));
  } catch {
    /* a private window keeps it in the editor only */
  }
  for (const f of listeners) f();
};
const listeners = new Set<() => void>();
export const onLocalOps = (f: () => void) => {
  listeners.add(f);
  return () => void listeners.delete(f);
};

/** Kept from the moment it is sent until its answer is known. */
export const rememberOp = (tenant: number, body: ShiftWrite & { op_id: string }) =>
  write([
    ...read().filter((o) => o.op_id !== body.op_id),
    { op_id: body.op_id, body, at: new Date().toISOString(), tenant },
  ]);
export const forgetOp = (opId: string) => write(read().filter((o) => o.op_id !== opId));
/**
 * Operations this page is sending now: their answer is still on its way, so they are not "without an
 * answer" yet. Listing them put the warning above the plan for every save and moved the whole plan
 * down and back (owner 18.10: "flicker"). Memory only: after a reload nothing is in flight.
 */
const sending = new Set<string>();
export const isSending = (opId: string) => sending.has(opId);
export const localOps = (tenant: number): CreateOp[] =>
  read().filter((o) => o.tenant === tenant && !sending.has(o.op_id));

/** The same operation again: the shift it made, or made now; a refusal is thrown as any write's is. */
export const resendOp = (body: ShiftWrite & { op_id: string }) => post<ApiShift>('/admin/shifts', body);

/**
 * Every create from the web app is one operation (PR #158 review: Vaktplan's quick add and copy
 * sent none): the caller's op_id, or a new one, kept in this browser until its
 * answer is known, so a lost answer shows on Vaktplan with «Sjekk» instead of the manager making
 * the shift again. A refusal is an answer and is forgotten. useShiftWrites().create runs it.
 */
export async function createOnce<R>(
  tenant: number | null | undefined,
  body: ShiftWrite,
  send: (b: ShiftWrite & { op_id: string }) => Promise<R>,
): Promise<R> {
  const sent = { ...body, op_id: body.op_id ?? newOpId() };
  sending.add(sent.op_id);
  if (tenant != null) rememberOp(tenant, sent);
  try {
    const made = await send(sent);
    forgetOp(sent.op_id);
    return made;
  } catch (e) {
    if (refused(e)) forgetOp(sent.op_id);
    throw e;
  } finally {
    // no answer: from now on it is listed; the listeners hear it
    sending.delete(sent.op_id);
    for (const f of listeners) f();
  }
}

/**
 * What a refused resend says. 'op_shift_gone' is no failure: the shift was made and deleted since
 * (PR #158 review), so the server's sentence stands alone.
 */
export const refusedText = (e: unknown) =>
  (e as { code?: string }).code === 'op_shift_gone'
    ? (e as Error).message
    : T('Kunne ikke lagre: {message}', { message: (e as Error).message });

/** This company's operations recorded on the server without a shift (GET /admin/shifts/create-ops). */
export function useServerOps(enabled: boolean) {
  return useQuery({
    queryKey: ['create-ops'],
    queryFn: () =>
      get<{ ops: { op_id: string; body: ShiftWrite; created_at: string }[] }>('/admin/shifts/create-ops'),
    enabled,
    staleTime: 30_000,
  });
}
